/**
 * セクション再生成ダイアログ。
 * PageOutline のカード上「再生成」ボタンと、編集 Sheet の「AI 最適化」タブから共有利用。
 *
 * 入力:
 *   - 追加指示 (instruction): 任意。プロンプトに「## Additional instruction」として注入
 *   - 現在の内容を踏襲する (useCurrentContent): デフォルト true。
 *       true:  現在の content を AI に渡してトーン・構成を踏襲して書き直し
 *       false: variant の sample のみ渡してブランド + キャンペーン情報からゼロベース生成
 */
import { Loader2, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label, Textarea } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useRegenerateLandingSection } from '@/hooks/useLandings';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  landingId: string;
  sectionId: string | null;
  /** 表示用ラベル（type + variant の人間可読名） */
  sectionLabel?: string;
}

export function LandingRegenerateDialog({
  open,
  onOpenChange,
  landingId,
  sectionId,
  sectionLabel,
}: Props) {
  const [instruction, setInstruction] = useState('');
  const [useCurrentContent, setUseCurrentContent] = useState(true);
  const regen = useRegenerateLandingSection();

  // 開閉のたびに入力をリセット
  useEffect(() => {
    if (!open) {
      setInstruction('');
      setUseCurrentContent(true);
    }
  }, [open]);

  const handleRegen = async () => {
    if (!sectionId) return;
    try {
      await regen.mutateAsync({
        landingId,
        sectionId,
        instruction: instruction.trim() || undefined,
        useCurrentContent,
      });
      toast.success('再生成完了');
      onOpenChange(false);
    } catch (e) {
      toast.error(`再生成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && regen.isPending) return;
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="size-4 text-[var(--accent)]" />
            AI で再生成
          </DialogTitle>
          <DialogDescription>
            {sectionLabel
              ? `「${sectionLabel}」を AI で書き直します。`
              : 'このセクションを AI で書き直します。'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-xs">
          <div className="space-y-1.5">
            <Label htmlFor="regen-instruction">追加指示 (任意)</Label>
            <Textarea
              id="regen-instruction"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder={
                '例: もっとカジュアルに / 英語で / 数字を強調 / B2B 寄りの表現に'
              }
              rows={4}
            />
            <p className="text-[10px] text-[var(--text-muted)]">
              ブランド情報・キャンペーン情報は自動的にプロンプトに含まれます。
              ここではトーン・着眼点など追加の指示だけを入力してください。
            </p>
          </div>

          <label
            htmlFor="regen-use-current"
            className="flex items-start gap-2 p-3 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] cursor-pointer"
          >
            <input
              id="regen-use-current"
              type="checkbox"
              checked={useCurrentContent}
              onChange={(e) => setUseCurrentContent(e.target.checked)}
              className="mt-0.5"
            />
            <div className="flex-1">
              <div className="text-xs font-semibold">
                現在の内容を踏襲する (推奨)
              </div>
              <div className="text-[10px] text-[var(--text-muted)] mt-1 leading-relaxed">
                {useCurrentContent
                  ? '現在のセクション内容を AI に渡し、トーンや構成を保ったまま書き直します。'
                  : 'ブランド + キャンペーン情報だけからゼロベースで再生成します。現在の内容は破棄されます。'}
              </div>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={regen.isPending}
          >
            キャンセル
          </Button>
          <Button onClick={handleRegen} disabled={regen.isPending || !sectionId}>
            {regen.isPending ? (
              <>
                <Loader2 className="size-3.5 mr-1 animate-spin" />
                再生成中...
              </>
            ) : (
              <>
                <RefreshCw className="size-3.5 mr-1" />
                AI で再生成
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
